import { useMemo } from 'react';
import { isFeatureEnabledForCountry } from '../utils/helpers.utils';
import { UACHelper } from '../utils/uac.utils';
import { UACItem } from '../constants/UACConstants';

type UACPermission = 'VIEW' | 'EDIT';

interface UACPermissionItem {
  key: string;
  permissions: string[];
  type: string;
  parentKey?: string;
}

/**
 * Custom hook for UAC (User Access Control) validation
 * Checks if user has required permissions for a specific UAC key
 * Delegates the actual rule to UACHelper so there is a single source of truth.
 */
export const useUACAccess = (
  uacKey: string | undefined,
  uacPermission: UACPermission = 'VIEW',
  uacPermissions: UACPermissionItem[] = []
): boolean => {
  return useMemo(() => {
    // If no UAC key specified, grant access by default
    if (!uacKey) return true;

    return new UACHelper(uacPermissions as UACItem[]).hasAccess(uacKey, uacPermission);
  }, [uacKey, uacPermission, uacPermissions]);
};

/**
 * Custom hook for feature country validation
 * Checks if a feature is enabled for a specific country
 */
export const useFeatureCountryAccess = (
  featuredCountry: string | undefined,
  countryCode: string | undefined
): boolean => {
  return useMemo(() => {
    // If no feature country check specified, grant access by default
    if (!featuredCountry || !countryCode) return true;

    return isFeatureEnabledForCountry(featuredCountry, countryCode);
  }, [featuredCountry, countryCode]);
};
